import { MouseEvent, useMemo } from "react";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import {
  InactiveStoreBranchResponse,
  InactiveStoreCountsResponse,
  InactiveStoreSummaryResponse,
} from "../../types/inactive-stores.type";
import { SURFACE } from "../common/designTokens";
import { formatCount } from "../VisitFrequency/visitFrequency";
import {
  MISSING,
  PivotCellSelection,
  PivotColumn,
  columnHeader,
  columnKey,
  countFor,
  pivotCellTone,
  pivotColumns,
  toneSx,
} from "./inactiveStores";

const TABLE_SX = { "& td, & th": { whiteSpace: "nowrap" } } as const;
const HEAD_SX = { "& th": { bgcolor: SURFACE.head, fontWeight: 600 } } as const;
const BRANCH_ROW_SX = { cursor: "pointer" } as const;
const COUNT_LINK_SX = { font: "inherit", fontWeight: "inherit" } as const;
/** สาขา and รหัส, ahead of the count columns. */
const IDENTITY_COLUMN_COUNT = 2;
const TOTALS_LABEL = "รวมทั้งหมด";
const NO_BRANCH_TEXT = "ไม่พบข้อมูลสาขา";

interface PivotTableProps {
  summary: InactiveStoreSummaryResponse;
  /** The detail table's branch filter, highlighted here; `ALL_OPTION` when none. */
  selectedBranchCode: string;
  onToggleBranch: (branchCode: string) => void;
  onOpenCell: (selection: PivotCellSelection) => void;
}

/**
 * The FIN Excel pivot: one row per branch at the chosen threshold, then รวมทั้งหมด. It always
 * counts every branch in scope and never follows the detail filters.
 */
export default function PivotTable({
  summary,
  selectedBranchCode,
  onToggleBranch,
  onOpenCell,
}: Readonly<PivotTableProps>) {
  const columns = useMemo(
    () => pivotColumns(summary.group_columns),
    [summary.group_columns],
  );
  const hasBranches = summary.branches.length > 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack mb={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          สรุปรายสาขา
        </Typography>
      </Stack>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table size="small" sx={TABLE_SX}>
          <TableHead sx={HEAD_SX}>
            <TableRow>
              <TableCell>สาขา</TableCell>
              <TableCell>รหัส</TableCell>
              {columns.map((column) => (
                <TableCell key={columnKey(column)} align="center">
                  {columnHeader(column)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasBranches && (
              <TableRow>
                <TableCell
                  colSpan={IDENTITY_COLUMN_COUNT + columns.length}
                  align="center"
                >
                  <Typography variant="body2" color="text.secondary" py={2}>
                    {NO_BRANCH_TEXT}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {summary.branches.map((branch) => (
              <PivotBranchRow
                key={branch.branch_code}
                branch={branch}
                columns={columns}
                isSelected={branch.branch_code === selectedBranchCode}
                onToggleBranch={onToggleBranch}
                onOpenCell={onOpenCell}
              />
            ))}
            {hasBranches && (
              <TableRow>
                <TableCell
                  colSpan={IDENTITY_COLUMN_COUNT}
                  sx={toneSx("totalRow")}
                >
                  {TOTALS_LABEL}
                </TableCell>
                <PivotCountCells
                  counts={summary.totals}
                  branch={null}
                  columns={columns}
                  isTotalsRow
                  onOpenCell={onOpenCell}
                />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

interface PivotBranchRowProps {
  branch: InactiveStoreBranchResponse;
  columns: PivotColumn[];
  isSelected: boolean;
  onToggleBranch: (branchCode: string) => void;
  onOpenCell: (selection: PivotCellSelection) => void;
}

function PivotBranchRow({
  branch,
  columns,
  isSelected,
  onToggleBranch,
  onOpenCell,
}: Readonly<PivotBranchRowProps>) {
  const handleRowClick = () => onToggleBranch(branch.branch_code);

  return (
    <TableRow
      hover
      selected={isSelected}
      onClick={handleRowClick}
      sx={BRANCH_ROW_SX}
    >
      <TableCell>{branch.branch_name ?? MISSING}</TableCell>
      <TableCell>{branch.branch_code}</TableCell>
      <PivotCountCells
        counts={branch}
        branch={branch}
        columns={columns}
        isTotalsRow={false}
        onOpenCell={onOpenCell}
      />
    </TableRow>
  );
}

interface PivotCountCellsProps {
  counts: InactiveStoreCountsResponse;
  /** `null` on the รวมทั้งหมด row. */
  branch: InactiveStoreBranchResponse | null;
  columns: PivotColumn[];
  isTotalsRow: boolean;
  onOpenCell: (selection: PivotCellSelection) => void;
}

function PivotCountCells({
  counts,
  branch,
  columns,
  isTotalsRow,
  onOpenCell,
}: Readonly<PivotCountCellsProps>) {
  return (
    <>
      {columns.map((column) => (
        <PivotCountCell
          key={columnKey(column)}
          selection={{ branch, column, count: countFor(counts, column) }}
          isTotalsRow={isTotalsRow}
          onOpenCell={onOpenCell}
        />
      ))}
    </>
  );
}

interface PivotCountCellProps {
  selection: PivotCellSelection;
  isTotalsRow: boolean;
  onOpenCell: (selection: PivotCellSelection) => void;
}

/** A count above zero opens the shops behind it; a zero has nothing to list. */
function PivotCountCell({
  selection,
  isTotalsRow,
  onOpenCell,
}: Readonly<PivotCountCellProps>) {
  const tone = pivotCellTone(selection.column, selection.count, isTotalsRow);
  // A coloured cell keeps its own text colour so the number stays readable on the tint.
  const linkColor = tone === null ? "primary" : "inherit";

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    // The row's own click filters the detail table; the count opens its shop list instead.
    event.stopPropagation();
    onOpenCell(selection);
  };

  return (
    <TableCell align="center" sx={toneSx(tone)}>
      {selection.count > 0 ? (
        <Link
          component="button"
          type="button"
          underline="always"
          color={linkColor}
          onClick={handleOpen}
          sx={COUNT_LINK_SX}
        >
          {formatCount(selection.count)}
        </Link>
      ) : (
        formatCount(selection.count)
      )}
    </TableCell>
  );
}
